import { useState } from "react";
import ReactDOM from "react-dom/client";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import Certificate from "../Certificate.tsx";
import { generateHashCode } from "../helpers/helpers.tsx";

interface TrainingData {
  trainingTime: number;
  firstName: string;
  lastName: string;
  trainingType: string;
  trainingResult: any;
  appVersion?: string;
  [key: string]: any;
}

export const useCertificateGenerator = () => {
  const [isGenerating, setIsGenerating] = useState(false);

  const generateCertificate = async (
    trainingData: TrainingData,
    userId: string
  ) => {
    if (isGenerating) return;
    setIsGenerating(true);

    /*Format: CPR-[INITIALS][YYMMDD][HASH]
     * Example: CPR-MB250811A3D9
     *
     * Parts:
     * 1. CPR:
     * - A static prefix.
     *
     * 2. [INITIALS]: (2 characters)
     * - The first letter of the user's first name and last name.
     * - Example: For a user "Max B", this part would be "MB".
     *
     * 3. [YYMMDD]: (6 characters)
     * - The date of the training session in YYMMDD format.
     * - Example: For August 11, 2025, this part would be "250811".
     *
     * 4. [HASH]: (3 characters)
     * - A 3-character uppercase hexadecimal string. It is a short, non-cryptographic
     * hash generated from a unique seed string to ensure the code's uniqueness.
     * - The seed is composed of: `${userId}-${trainingTime}` (user's unique ID and the
     * exact training timestamp in milliseconds).
     */

    const date = new Date(trainingData.trainingTime);
    const year = String(date.getFullYear()).slice(-2);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateString = `${year}${month}${day}`;
    const initials = `${trainingData.firstName?.[0] || "X"}${
      trainingData.lastName?.[0] || "X"
    }`.toUpperCase();
    const uniqueSeed = `${userId}-${trainingData.trainingTime}`;
    const hashCode = generateHashCode(uniqueSeed).slice(0, 3);
    const certificateCode = `CPR-${initials}${dateString}${hashCode}`;

    const container = document.createElement("div");
    container.style.position = "absolute";
    container.style.left = "-10000px";
    container.style.width = "1170px";
    container.style.height = "827px";
    document.body.appendChild(container);

    const root = ReactDOM.createRoot(container);
    root.render(
      <Certificate
        data={{
          ...trainingData,
          certificateCode: certificateCode,
        }}
      />
    );

    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));

      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        width: 1170,
        height: 827,
      });

      const imgData = canvas.toDataURL("image/png", 1.0);
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });
      pdf.setProperties({ title: certificateCode });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);

      const pdfBlob = pdf.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `${certificateCode}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(pdfUrl), 10000);
    } catch (error) {
      console.error("PDF generation error:", error);
      alert("Certificate generation failed. Please try again.");
    } finally {
      root.unmount();
      document.body.removeChild(container);
      setIsGenerating(false);
    }
  };

  return { isGenerating, generateCertificate };
};
